import { useDropzone } from "@uploadthing/react";
import { useCallback, useState } from "react";
import { FileWithPath } from "react-dropzone";
import toast from "react-hot-toast";
import { PiPencilSimple } from "react-icons/pi";
import { FieldError, Loader, Text } from "rizzui";
import { generateClientDropzoneAccept } from "uploadthing/client";
import { ClientUploadedFileData } from "uploadthing/types";
import UploadIcon from "../../components/shape/upload";
import { LoadingSpinner } from "../../ui/file-upload/upload-zone";
import cn from "../../utils/class-names";
import { useUploadThing } from "../../utils/uploadthing";

interface UploadZoneProps {
    name: string;
    getValues?: any;
    setValue?: any;
    className?: string;
    error?: string;
}

export default function AvatarUpload({
    name,
    error,
    className,
    getValues,
    setValue,
}: UploadZoneProps) {
    const [files, setFiles] = useState<File[]>([]);

    const formValue = getValues(name);

    const { startUpload, routeConfig, isUploading } = useUploadThing("avatar", {
        onClientUploadComplete: (
            res: ClientUploadedFileData<any>[] | undefined,
        ) => {
            if (setValue) {
                const respondedUrls = res?.map((r) => ({
                    name: r.name,
                    size: r.size,
                    url: r.url,
                }));
                setValue(name, respondedUrls?.[0]);
            }
            toast.success(
                <Text as="b" className="font-semibold">
                    Avatar updated
                </Text>,
            );
        },
        onUploadError: (error: Error) => {
            console.error(error);
            toast.error(error.message);
        },
    });

    const fileTypes = routeConfig ? Object.keys(routeConfig) : [];

    const onDrop = useCallback(
        (acceptedFiles: FileWithPath[]) => {
            setFiles([
                ...acceptedFiles.map((file) =>
                    Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    }),
                ),
            ]);
            startUpload(files);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [files],
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: fileTypes ? generateClientDropzoneAccept(fileTypes) : undefined,
    });

    return (
        <div className={cn("grid gap-5", className)}>
            <div
                className={cn(
                    "relative grid h-40 w-40 place-content-center rounded-full border-[1.8px]",
                )}
            >
                {formValue ? (
                    <>
                        <figure className="absolute inset-0 rounded-full">
                            <img
                                // fill
                                alt="user avatar"
                                src={formValue?.url}
                                className="rounded-full"
                            />
                        </figure>
                        <div
                            {...getRootProps()}
                            className={cn(
                                "absolute inset-0 grid place-content-center rounded-full bg-black/70",
                            )}
                        >
                            {isUploading ? (
                                <LoadingSpinner />
                            ) : (
                                <PiPencilSimple className="h-5 w-5 text-white" />
                            )}

                            <input {...getInputProps()} />
                        </div>
                    </>
                ) : (
                    <div
                        {...getRootProps()}
                        className={cn(
                            "absolute inset-0 z-10 grid cursor-pointer place-content-center",
                        )}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon className="mx-auto h-12 w-12" />

                        {isUploading ? (
                            <Loader
                                variant="spinner"
                                className="justify-center"
                            />
                        ) : (
                            <Text className="font-medium">
                                Drop or select file
                            </Text>
                        )}
                    </div>
                )}
            </div>
            {error && <FieldError error={error} />}
        </div>
    );
}
